<template>
	<div class="cart-features">
		<div class="row">
			<div class="col-md-12 col-md-offset-1">
				<h5 class="cart-features-heading">
					Includes
				</h5>
				<div class="cart-features-container">
					<div class="first-row">
						<feature-icon
							v-for="feature in activeFeatures"
							:key="`active-${feature.id}`"
							:feature="feature"
						/>
					</div>
					<div class="second-row">
						<feature-icon
							v-for="feature in inactiveFeatures"
							:key="`inactive-${feature.id}`"
							:feature="feature"
						/>
					</div>
				</div>
				<div v-if="!allAccessoriesAreSelected" class="cart-accessories">
					<div class="row">
						<div class="col-md-12">
							<button
								class="btn btn-purple btn-outline cart-accessories-btn"
								@click="goToAccessories"
							>
								Add Accessories
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import _isEmpty from "lodash/isEmpty";
import FeatureIcon from "./feature-icon";

export default {
	name: "CartFeatures",
	components: {
		FeatureIcon
	},
	props: {
		prebuildParts: {
			type: Object,
			default() {
				return {};
			}
		},
		accessories: {
			type: [Object, Array],
			default() {
				return {};
			}
		},
		currentAccessoriesCart: {
			type: [Object, Array],
			default() {
				return {};
			}
		}
	},
	computed: {
		allAccessoriesAreSelected() {
			return this.inactiveFeatures.every(feature => feature.isActive);
		},
		featuresActive() {
			if (!_isEmpty(this.prebuildParts)) {
				const motherboardBluetooth =
					this.prebuildParts.motherboards.bluetooth === "1";
				const extrasBluetooth = this.prebuildParts.extras.some(
					extra => extra.bluetooth === "1"
				);
				const motherboardsWifi = this.prebuildParts.motherboards.wifi === "1";
				const extrasWifi = this.prebuildParts.extras.some(
					extra => extra.wifi == "1"
				);
				const vrReady =
					this.prebuildParts.gpus.vr_capable === "1" &&
					this.prebuildParts.cpus.vr_capable === "1";
				const usbC =
					this.prebuildParts.motherboards.external_usb_type_c === "1";
				const streamReady = this.prebuildParts.ram.streaming_ready;

				return {
					bluetooth: motherboardBluetooth || extrasBluetooth,
					wifi: motherboardsWifi || extrasWifi,
					vrReady,
					usbC,
					streamReady
				};
			}

			return {};

		},
		checkMonitor() {
			if (!_isEmpty(this.accessories) && !_isEmpty(this.currentAccessoriesCart) && this.accessories.monitors) {
				const monitorsId = this.accessories.monitors.map(monitor => monitor.id);
				// eslint-disable-next-line camelcase
				const isMonitor = this.currentAccessoriesCart.some(({ product_id }) => monitorsId.includes(product_id));
				return isMonitor;
			}

			return false;
		},
		activeFeatures() {
			return [
				{
					id: 1,
					image: {
						active: "/images/icons/cart-icons/lighting-active.svg",
						inactive: "/images/icons/cart-icons/lighting-inactive.svg"
					},
					text: "RGB Lighting",
					tooltip: {
						included: "This build is equipped with RGB components.",
						missing: "This build is not equipped with RGB components."
					},
					isActive: true
				},
				{
					id: 2,
					image: {
						active: "/images/icons/cart-icons/signal-wifi-active.svg",
						inactive: "/images/icons/cart-icons/signal-wifi-inactive.svg"
					},
					text: "WiFi",
					tooltip: {
						included: "This build is WIFI ready.",
						missing: "This build does not have built-in WiFi capability."
					},
					isActive: this.featuresActive.wifi
				},
				{
					id: 3,
					image: {
						active: "/images/icons/cart-icons/bluetooth-active.svg",
						inactive: "/images/icons/cart-icons/bluetooth-inactive.svg"
					},
					text: "Bluetooth",
					tooltip: {
						included: "This build has Bluetooth capability for use with wireless Bluetooth products.",
						missing: "This build does not have Bluetooth capability for use with wireless Bluetooth products."
					},
					isActive: this.featuresActive.bluetooth
				},
				{
					id: 4,
					image: {
						active: "/images/icons/cart-icons/usb-type-c-active.svg",
						inactive: "/images/icons/cart-icons/usb-type-c-inactive.svg"
					},
					text: "USB-C",
					tooltip: {
						included: "USB-C port on the front I/O panel of the case is functional.",
						missing: "USB-C port on the front I/O panel of the case is not functional."
					},
					isActive: this.featuresActive.usbC
				},
				{
					id: 5,
					image: {
						active: "/images/icons/cart-icons/dude-active.svg",
						inactive: "/images/icons/cart-icons/dude-inactive.svg"
					},
					text: "VR Ready",
					tooltip: {
						included: "This build meets the minimum recommended specs for VR functionality.",
						missing: "VR is not recommended for this configuration."
					},
					isActive: this.featuresActive.vrReady
				}
			];
		},
		inactiveFeatures() {
			return [
				{
					id: 6,
					image: {
						active: "/images/icons/cart-icons/streaming-active.svg",
						inactive: "/images/icons/cart-icons/streaming-inactive.svg"
					},
					text: "Stream Ready",
					tooltip: {
						included: "This build will be capable of streaming most games in 720p with 60fps (internet speeds permitting). Games can only be streamed at 60+ FPS when running at 60+ FPS.",
						missing: "Streaming not recommended for this build."
					},
					isActive: this.featuresActive.streamReady
				},
				{
					id: 7,
					image: {
						active: "/images/icons/cart-icons/display-active.svg",
						inactive: "/images/icons/cart-icons/display-inactive.svg"
					},
					text: "Monitor",
					tooltip: {
						included: "You have a monitor in your cart.",
						missing: "You do not have a monitor with your build."
					},
					isActive: this.checkMonitor
				},
				{
					id: 8,
					image: {
						active: "/images/icons/cart-icons/headset-active.svg",
						inactive: "/images/icons/cart-icons/headset-inactive.svg"
					},
					text: "Headset",
					tooltip: {
						included: "You have a headset in your cart.",
						missing: "You do not have a headset with your build."
					},
					isActive: this.checkPeripheral("Headset")
				},
				{
					id: 9,
					image: {
						active: "/images/icons/cart-icons/keyboard-active.svg",
						inactive: "/images/icons/cart-icons/keyboard-inactive.svg"
					},
					text: "Keyboard",
					tooltip: {
						included: "You have a keyboard in your cart.",
						missing: "You do not have a keyboard with your build."
					},
					isActive: this.checkPeripheral("Keyboard") || this.checkPeripheral("Other")
				},
				{
					id: 10,
					image: {
						active: "/images/icons/cart-icons/mouse-active.svg",
						inactive: "/images/icons/cart-icons/mouse-inactive.svg"
					},
					text: "Mouse",
					tooltip: {
						included: "You have a mouse in your cart.",
						missing: "You do not have a mouse with your build."
					},
					isActive: this.checkPeripheral("Mouse") || this.checkPeripheral("Other")
				}
			];
		}
	},
	methods: {
		checkPeripheral(peripheralName) {
			if (!_isEmpty(this.currentAccessoriesCart)) {
				const peripherals = this.currentAccessoriesCart.map(peripheral => peripheral.peripheral_type);
				return peripherals.includes(peripheralName);
			}

			return false;
		},
		goToAccessories() {
			this.$router.push({ name: "starter-accessories" });
		}
	}
};
</script>

<style lang="scss" scoped>
@import "~@s/_asset-mixers.scss";

.cart-features {
	.col-md-offset-1 {
		@media (min-width: 992px) {
			margin-left: 13.333333%;
		}
	}
	.col-md-5 {
		padding-left: 0;
	}

	&-heading {
		margin: 0 0 25px;
		color: #141226;
		font-size: 16px;
		font-weight: bold;

		@media (min-width: 320px) and (max-width: 580px) {
			margin: 0 0 15px;
		}
	}

	&-container {
		display: flex;
		flex-direction: column;

		.first-row {
			margin-bottom: 13px;
		}

		.first-row,
		.second-row {
			display: flex;

			@media (min-width: 320px) and (max-width: 580px) {
				flex-wrap: wrap;
			}
		}
	}
}

.cart-accessories {
	.col-md-offset-1 {
		@media (min-width: 992px) {
			margin-left: 13.333333%;
		}
	}

	&-btn {
		border: 3px solid $purple;
		color: $purple;
		margin-bottom: 45px;
		margin-top: 13px;
		padding: 6px 33px;

		&:hover {
			background-color: $purple;
			border: 3px solid $purple;
			color: $white;
		}
	}
}
</style>
